import { RouteInfo } from "@/components/context-providers/RouteInfoProvider";
import { useTeamContext } from "@/components/context-providers/TeamProvider";
import { Environment } from "@/gen_src/proto/chalk/server/v1/environment_pb";
import {
  GetTeamPermissionsResponse,
  GetTeamResponse,
  Project,
  Team,
} from "@/gen_src/proto/chalk/server/v1/team_pb";
import {
  CloudProviderType,
  cloudProviderTypeFromEnvironmentPB,
  isServerlessEnvironmentPB,
} from "@/utils/environment";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { TeamService, TeamServiceNullImpl } from "@/utils/teamService";
import { ConnectError } from "@connectrpc/connect";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { PropsWithChildren, createContext, useMemo } from "react";

export interface RouteAndTeamInfo extends RouteInfo {
  loading: boolean;
  error?: ConnectError;
  team?: Team;
  teamService: TeamService;
  project?: Project;
  environment?: Environment;
  activeDeploymentId?: string;
  refetchTeamQuery: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<GetTeamResponse, ConnectError>>;
  refetchTeamPermissionsQuery: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<GetTeamPermissionsResponse, ConnectError>>;
  cloudProvider?: CloudProviderType;
  /** Whether the current in-scope environment runs on a serverless cluster. */
  isServerlessEnvironment?: boolean;
}

export const RouteAndTeamContext = createContext<RouteAndTeamInfo | undefined>(undefined);

export const useComputedRouteAndTeamInfo = (): RouteAndTeamInfo => {
  const routeInfo = useRouteInfo();
  const { projectId, environmentId } = routeInfo;
  const {
    team,
    teamPermissions,
    teamError,
    teamPermissionsError,
    teamLoading,
    teamPermissionsLoading,
    teamService,
    refetchTeamQuery,
    refetchTeamPermissionsQuery,
  } = useTeamContext();

  const error = teamError || teamPermissionsError || undefined;
  const loading = teamLoading || teamPermissionsLoading || !routeInfo.isReady;
  const project = useMemo(
    () => (projectId != null ? teamService.projectById(projectId) : undefined),
    [projectId, teamService],
  );
  const environment = useMemo(
    () => (environmentId != null ? teamService.environmentById(environmentId) : undefined),
    [environmentId, teamService],
  );
  const activeDeploymentId = environment?.activeDeploymentId ?? undefined;
  const cloudProvider = useMemo(
    () => (environment != null ? cloudProviderTypeFromEnvironmentPB(environment) : undefined),
    [environment],
  );
  const isServerlessEnvironment = useMemo(
    () => (environment != null ? isServerlessEnvironmentPB(environment) : undefined),
    [environment],
  );

  const contextValue = useMemo<RouteAndTeamInfo>(() => {
    if (error != null) {
      return {
        error,
        loading,
        teamService,
        refetchTeamQuery,
        refetchTeamPermissionsQuery,
        ...routeInfo,
      };
    }

    if (team == null || teamPermissions == null) {
      return {
        loading: true,
        teamService,
        refetchTeamQuery,
        refetchTeamPermissionsQuery,
        ...routeInfo,
      };
    }

    return {
      loading,
      team,
      project,
      environment,
      cloudProvider,
      isServerlessEnvironment,
      activeDeploymentId,
      teamService,
      refetchTeamQuery,
      refetchTeamPermissionsQuery,
      ...routeInfo,
    };
  }, [
    activeDeploymentId,
    cloudProvider,
    isServerlessEnvironment,
    environment,
    error,
    loading,
    project,
    refetchTeamPermissionsQuery,
    refetchTeamQuery,
    routeInfo,
    team,
    teamPermissions,
    teamService,
  ]);
  return contextValue;
};

export const RouteAndTeamInfoProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const routeAndTeamInfo = useComputedRouteAndTeamInfo();
  return (
    <RouteAndTeamContext.Provider value={routeAndTeamInfo}>{children}</RouteAndTeamContext.Provider>
  );
};

/**
 * Only intended for use in /test-components
 */
export const MockRouteAndTeamInfoProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const mockRouteAndTeamInfo = useMemo((): RouteAndTeamInfo => {
    return {
      loading: false,
      teamService: TeamServiceNullImpl,
      refetchTeamQuery: async () => ({ data: undefined, error: undefined }) as any,
      refetchTeamPermissionsQuery: async () => ({ data: undefined, error: undefined }) as any,
      isReady: true,
    };
  }, []);
  return (
    <RouteAndTeamContext.Provider value={mockRouteAndTeamInfo}>
      {children}
    </RouteAndTeamContext.Provider>
  );
};
